import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { CircularProgress, Box } from '@mui/material';
import { fetchInitialDataRequest } from '@app/store/appState';
import useImpersonationSession from '@app/hooks/useImpersonationSession';
import useArtemisWebRedirect from '@app/hooks/useArtemisWebRedirect';

// TODO(refactor): Migrate to React Router data loader once the data layer moves off Redux Sagas.
// Rationale: This component-based gate fetches initial data via useEffect + saga dispatch,
// bypassing the data router's loader mechanism. A loader would provide data-before-render
// semantics and route-level error handling, but the current saga orchestration (Okta, LD
// identify, profile, company accesses) depends on React context and has no clean async
// interface a loader can await. Bridging via store.subscribe/timeout (see initialDataLoader.js)
// is fragile. Migration path: adopt RTK Query (or plain async fetchers) for initial data,
// inject dependencies via loader factory closures, and use defer() for non-blocking UX.
const InitialDataGate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const ldClient = useLDClient();
  const initialDataAlreadyFetched = useSelector(
    state => state.appState.initialDataAlreadyFetched,
  );
  const {
    isSuperAdmin,
    isImpersonationSession,
    isPendingImpersonationSession,
  } = useImpersonationSession();
  const { shouldBlockRender: isArtemisRedirectPending } =
    useArtemisWebRedirect();

  useEffect(() => {
    if (!initialDataAlreadyFetched) {
      const userId = authState?.accessToken?.claims?.UserId ?? '';
      const identify = ldClient?.identify
        ? (context, hash, callback) => {
            if (callback) {
              ldClient.identify(context, hash, callback);
              return undefined;
            }
            return ldClient.identify(context, hash);
          }
        : (context, hash, callback) => {
            if (callback) {
              callback(null, {});
            }
            return Promise.resolve({});
          };

      dispatch(
        fetchInitialDataRequest({
          navigate: path => navigate(path, { replace: true }),
          identify,
          userId,
        }),
      );
    }
    // Intentionally run only on mount; payload uses current auth/LD at that time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isSuperAdmin &&
      !isPendingImpersonationSession &&
      !isImpersonationSession
    ) {
      navigate('/impersonate', { replace: true });
    }
  }, [
    isSuperAdmin,
    isPendingImpersonationSession,
    isImpersonationSession,
    navigate,
  ]);

  const showLoader =
    isArtemisRedirectPending ||
    isPendingImpersonationSession ||
    (isSuperAdmin && !isImpersonationSession);

  if (showLoader) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return <Outlet />;
};

export default InitialDataGate;
